import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import Loader from "../../../../../components/Loader";
import toEnglishDigits from "../../../../../utils/farsiToEnglishDigits";
import fetchApi from "../../../../../utils/fetchApi";

export default function RegisterCategory() {
  const { catId } = useParams();
  const navigate = useNavigate();

  // #region States
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [categoryTypes, setCategoryTypes] = useState([]);
  const [isDisabled, setIsDisabled] = useState(false);

  // Edit Event
  const [presentEvent, setPresentEvent] = useState({});
  // #endregion region

  const orderMap = {
    1: "امتیازی",
    2: "حضوری",
    3: "پیاده روی",
    4: "دوچرخه سواری",
  };

  //   #region ==API==> Get Present Event-Category By Id

  //   #region ==API==> Get All Event-Category-Types

  useEffect(() => {
    setInitialLoading(true);
    (async () => {
      try {
        const data = await fetchApi(
          "GET",
          `${
            window?.location?.origin?.includes("localhost")
              ? `${process.env.REACT_APP_DEV_URL}`
              : `${process.env.REACT_APP_API_URL}`
          }/organizations/event-category-types`,
          {
            params: {
              isActive: true,
            },
          }
        );
        const sorted = [...data?.data].sort((a, b) => {
          const orderA = orderMap[a.name] ?? 9999; // unknowns go last
          const orderB = orderMap[b.name] ?? 9999;
          return orderA - orderB;
        });

        setCategoryTypes(sorted);
      } finally {
        setInitialLoading(false);
      }
    })();
  }, []);

  // #endregion

  useEffect(() => {
    if (catId) {
      setInitialLoading(true);
      (async () => {
        try {
          const data = await fetchApi(
            "GET",
            `${
              window?.location?.origin?.includes("localhost")
                ? `${process.env.REACT_APP_DEV_URL}`
                : `${process.env.REACT_APP_API_URL}`
            }/organizations/event-categories/${catId}`,
            {
              headers: {},
              params: {
                orgId: localStorage.getItem("orgId"),
                id: catId,
              },
            }
          );
          setPresentEvent(data?.data);
        } finally {
          setInitialLoading(false);
        }
      })();
    }
  }, []);

  // #endregion

  const asNumber = label =>
    Yup.number()
      .transform((v, orig) => (orig === "" || orig == null ? undefined : v))
      .typeError(`${label} باید عدد باشد`);

  const buildSchema = isDisabled => {
    const reqStr = (schema, msg) =>
      isDisabled ? schema.notRequired() : schema.required(msg);
    const reqNum = (schema, msg) =>
      isDisabled ? schema.notRequired() : schema.required(msg);

    return Yup.object({
      // always required
      name: Yup.string().required("نام دسته‌بندی رویداد الزامی است."),

      // optional when isDisabled === true
      indicator: reqStr(Yup.string(), "شاخص الزامی است."),
      indicatorMeasurementUnit: reqStr(
        Yup.string(),
        "واحد اندازه‌گیری شاخص الزامی است."
      ),

      // optional when isDisabled === true (even if employeeCreateEvent = true)
      creatorGainChallengeScorePercent: asNumber("درصد امتیاز برای چالش")
        .min(0, "حداقل ۰")
        .max(100, "حداکثر ۱۰۰")
        .when("employeeCreateEvent", (employeeCreateEvent, schema) => {
          if (isDisabled) return schema.notRequired();
          return employeeCreateEvent
            ? schema.required("درصد امتیاز برای چالش الزامی است.")
            : schema.notRequired();
        }),

      // optional when isDisabled === true
      challengeParticipationPercent: reqNum(
        asNumber("درصد امتیاز برای شرکت‌کنندگان"),
        "درصد امتیاز برای شرکت‌کنندگان الزامی است."
      ),
      firstPlacePercent: reqNum(
        asNumber("درصد امتیاز نفر اول"),
        "درصد امتیاز نفر اول الزامی است."
      )
        .min(0, "حداقل ۰")
        .max(100, "حداکثر ۱۰۰"),
      secondPlacePercent: reqNum(
        asNumber("درصد امتیاز نفر دوم"),
        "درصد امتیاز نفر دوم الزامی است."
      )
        .min(0, "حداقل ۰")
        .max(100, "حداکثر ۱۰۰"),
      thirdPlacePercent: reqNum(
        asNumber("درصد امتیاز نفر سوم"),
        "درصد امتیاز نفر سوم الزامی است."
      )
        .min(0, "حداقل ۰")
        .max(100, "حداکثر ۱۰۰"),

      // keep typeId optional (or add required here if you want it enforced)
      // typeId: isDisabled ? Yup.string().notRequired() : Yup.string().required("نوع دسته‌بندی را انتخاب کنید."),
    });
  };

  const validationSchema = useMemo(() => buildSchema(isDisabled), [isDisabled]);

  const register = useFormik({
    validateOnChange: false,
    initialValues: {
      name: "",
      indicator: "",
      indicatorMeasurementUnit: "",
      creatorGainChallengeScorePercent: 0,
      creatorGainParticipantsScorePercent: 0,
      challengeParticipationPercent: 0,
      firstPlacePercent: "",
      secondPlacePercent: "",
      thirdPlacePercent: "",
      employeeCreateEvent: false, // default value
      typeId: "",
      orgId: localStorage?.getItem("orgId"),
      isActive: true,
      // dataEntryType: "PRESENCE", // <-- default value
    },
    validationSchema,
    onSubmit: async values => {
      const normalized = {
        creatorGainChallengeScorePercent: values.employeeCreateEvent
          ? Number(values.creatorGainChallengeScorePercent || 0)
          : 0,
        creatorGainParticipantsScorePercent: values.employeeCreateEvent
          ? Number(values.creatorGainParticipantsScorePercent || 0)
          : 0,
      };

      const payload = {
        ...(!catId && { orgId: values?.orgId }),
        typeId: values.typeId,
        name: values.name,
        indicator: values.indicator,
        indicatorMeasurementUnit: values.indicatorMeasurementUnit,
        creatorGainChallengeScorePercent:
          normalized.creatorGainChallengeScorePercent,
        creatorGainParticipantsScorePercent:
          normalized.creatorGainParticipantsScorePercent,
        challengeParticipationPercent: values.challengeParticipationPercent,
        firstPlacePercent: values.firstPlacePercent,
        secondPlacePercent: values.secondPlacePercent,
        thirdPlacePercent: values.thirdPlacePercent,
        employeeCreateEvent: values.employeeCreateEvent,
        isActive: values.isActive,
        // dataEntryType: values.dataEntryType,
      };
      setIsLoading(true);
      if (catId) {
        // Update if catId is present (PATCH)
        try {
          await fetchApi(
            "PATCH",
            `${
              window?.location?.origin?.includes("localhost")
                ? `${process.env.REACT_APP_DEV_URL}`
                : `${process.env.REACT_APP_API_URL}`
            }/organizations/event-categories/${catId}`,
            {
              headers: {},
              data: payload,
              params: {
                orgId: localStorage?.getItem("orgId"),
              },
            }
          );
          toast.success("دسته‌بندی با موفقیت به روز رسانی شد.");
          setTimeout(() => {
            navigate(`/dashboard/challenges/category`);
          }, 2000);
        } catch (error) {
          toast.error("خطا در به روز رسانی دسته‌بندی.");
        } finally {
          setIsLoading(false);
        }
      } else {
        // Create new category (POST)
        try {
          await fetchApi(
            "POST",
            `${
              window?.location?.origin?.includes("localhost")
                ? `${process.env.REACT_APP_DEV_URL}`
                : `${process.env.REACT_APP_API_URL}`
            }/organizations/event-categories`,
            {
              headers: {},
              data: payload,
            }
          );
          toast.success("دسته‌بندی جدید با موفقیت ایجاد شد.");
          setTimeout(() => {
            navigate(`/dashboard/challenges/category`);
          }, 2000);
        } catch (error) {
          toast.error("خطا در ایجاد دسته‌بندی.");
        } finally {
          setIsLoading(false);
        }
      }
    },
  });

  const isPresenceTypeSelected = useMemo(
    () =>
      categoryTypes
        ?.filter(c => c?.dataEntryType === "PRESENCE")
        .some(j => j.id === register?.values?.typeId),
    [categoryTypes, register?.values?.typeId]
  );

  const isSystemTypeSelected = useMemo(
    () =>
      categoryTypes
        ?.filter(c => c?.dataEntryType === "SYSTEM")
        .some(j => j.id === register?.values?.typeId),
    [categoryTypes, register?.values?.typeId]
  );

  const isRewardTypeSelected = useMemo(
    () =>
      categoryTypes
        ?.filter(c => c?.dataEntryType === "ADMIN" && c?.name === "امتیازی")
        .some(j => j.id === register?.values?.typeId),
    [categoryTypes, register?.values?.typeId]
  );

  useEffect(() => {
    if (presentEvent) {
      register.setFieldValue("name", presentEvent?.name ?? "", true);
      register.setFieldValue("indicator", presentEvent?.indicator ?? "", true);
      register.setFieldValue(
        "indicatorMeasurementUnit",
        presentEvent?.indicatorMeasurementUnit ?? "",
        true
      );
      register.setFieldValue(
        "creatorGainChallengeScorePercent",
        presentEvent?.creatorGainChallengeScorePercent ?? 0, // ← ۰
        true
      );
      register.setFieldValue(
        "creatorGainParticipantsScorePercent",
        presentEvent?.creatorGainParticipantsScorePercent ?? 0, // ← ۰
        true
      );
      register.setFieldValue(
        "challengeParticipationPercent",
        presentEvent?.challengeParticipationPercent ?? "",
        true
      );
      register.setFieldValue(
        "firstPlacePercent",
        presentEvent?.firstPlacePercent ?? "",
        true
      );
      register.setFieldValue(
        "secondPlacePercent",
        presentEvent?.secondPlacePercent ?? "",
        true
      );
      register.setFieldValue(
        "thirdPlacePercent",
        presentEvent?.thirdPlacePercent ?? "",
        true
      );
      register.setFieldValue(
        "employeeCreateEvent",
        presentEvent?.employeeCreateEvent ?? false,
        true
      );
      register.setFieldValue("isActive", presentEvent?.isActive ?? true, true);
      register.setFieldValue("typeId", presentEvent?.type?.id ?? "", true);
      register.setFieldValue(
        "dataEntryType",
        presentEvent?.type?.dataEntryType || "PRESENCE",
        true
      );
    }
  }, [presentEvent]);

  useEffect(() => {
    if (!register.values.employeeCreateEvent) {
      register.setFieldValue("creatorGainChallengeScorePercent", 0, false);
      register.setFieldValue("creatorGainParticipantsScorePercent", 0, false);
    }
  }, [register.values.employeeCreateEvent]);

  // Farsi Digits Sanitization
  const handleNumericChange = field => e => {
    const raw = e.target.value;
    const normalized = toEnglishDigits(raw).replace(/[^\d]/g, ""); // keep digits only
    const capped =
      field === "challengeParticipationPercent" ||
      field === "firstPlacePercent" ||
      field === "secondPlacePercent" ||
      field === "thirdPlacePercent"
        ? Math.min(Number(normalized || 0), 100).toString()
        : normalized;
    register.setFieldValue(field, capped);
  };

  useEffect(() => {
    if (isPresenceTypeSelected) {
      if (Number(register.values.challengeParticipationPercent) !== 100) {
        register.setFieldValue("challengeParticipationPercent", "100", true);
      }
      if (Number(register.values.firstPlacePercent) !== 0) {
        register.setFieldValue("firstPlacePercent", "0", true);
      }
      if (Number(register.values.secondPlacePercent) !== 0) {
        register.setFieldValue("secondPlacePercent", "0", true);
      }
      if (Number(register.values.thirdPlacePercent) !== 0) {
        register.setFieldValue("thirdPlacePercent", "0", true);
      }
    }
  }, [
    isPresenceTypeSelected,
    register.values.challengeParticipationPercent,
    register.values.firstPlacePercent,
    register.values.secondPlacePercent,
    register.values.thirdPlacePercent,
  ]);

  useEffect(() => {
    if (isRewardTypeSelected) {
      register.setFieldValue("indicator", "امتیازی", true);
      register.setFieldValue("indicatorMeasurementUnit", "امتیازی", true);
    }
  }, [isRewardTypeSelected]);

  useEffect(() => {
    if (
      categoryTypes?.find(j => j.name === "امتیازی")?.id ===
      register?.values?.typeId
    ) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [categoryTypes, register?.values?.typeId]);

  return (
    <>
      {initialLoading ? (
        <Loader />
      ) : (
        <div
          className={`w-full flex flex-col h-[calc(100vh-192px)] overflow-y-auto scrollBar px-2`}
        >
          <div
            className={`w-full pb-3 lg:pb-7 border-b border-myNeutralBorder flex items-center gap-4`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M10.4166 18.3335H3.39993C2.43327 18.3335 1.6416 17.5585 1.6416 16.6085V4.2418C1.6416 2.05846 3.2666 1.06679 5.25827 2.04179L8.95827 3.85846C9.75827 4.25013 10.4166 5.29179 10.4166 6.17513V18.3335Z"
                stroke="#414A53"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M18.3083 12.55V15.7C18.3083 17.5 17.475 18.3333 15.675 18.3333H10.4166V8.68335L10.8083 8.76668L14.5583 9.60835L16.25 9.98335C17.35 10.225 18.25 10.7917 18.3 12.3917C18.3083 12.4417 18.3083 12.4917 18.3083 12.55Z"
                stroke="#414A53"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4.58325 7.5H7.47492"
                stroke="#414A53"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4.58325 10.8335H7.47492"
                stroke="#414A53"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14.5583 9.60851V12.2918C14.5583 13.3252 13.7167 14.1668 12.6833 14.1668C11.65 14.1668 10.8083 13.3252 10.8083 12.2918V8.76685L14.5583 9.60851Z"
                stroke="#414A53"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M18.3 12.3917C18.25 13.3751 17.4333 14.1667 16.4333 14.1667C15.4 14.1667 14.5583 13.3251 14.5583 12.2917V9.6084L16.25 9.9834C17.35 10.2251 18.25 10.7917 18.3 12.3917Z"
                stroke="#414A53"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {catId ? (
              <span className={`text-myBlack414 text-sm lg:text-lg font-bold`}>
                ویرایش دسته‌بندی{" "}
                <strong
                  dir="ltr"
                  className={`font-black text-xl text-myBlue257`}
                >
                  " {presentEvent?.name} "
                </strong>
              </span>
            ) : (
              <span className={`text-myBlack414 text-sm lg:text-lg font-bold`}>
                ثبت دسته‌بندی جدید
              </span>
            )}
          </div>
          <div className={`mt-8 flex justify-between gap-8`}>
            <form
              onSubmit={register.handleSubmit}
              className={`flex flex-col gap-7 w-full`}
            >
              <div
                className={`flex max-lg:flex-wrap items-center gap-2 w-full`}
              >
                <div className={`flex items-center gap-4 w-full lg:w-1/2`}>
                  <span className={`text-[#6f6f6f] text-sm whitespace-pre`}>
                    نام دسته‌بندی چالش
                  </span>
                  <input
                    {...register.getFieldProps("name")}
                    className={`input w-full bg-white rounded-lg border text-black ${
                      register.touched.name && register.errors.name
                        ? "border-red-500"
                        : "border-[#D6D6D6]"
                    }`}
                    placeholder="نام دسته بندی چالش"
                  />
                </div>
                <div className={`flex items-center gap-4 w-full lg:w-1/2`}>
                  <span className={`text-[#6f6f6f] text-sm whitespace-pre`}>
                    نوع دسته‌بندی
                  </span>
                  <select
                    {...register.getFieldProps("typeId")}
                    className={`select select-bordered bg-white text-black w-full ${
                      register.touched.typeId && register.errors.typeId
                        ? "border-red-500"
                        : "border-[#D6D6D6]"
                    }`}
                  >
                    <option value="" disabled>
                      انتخاب کنید
                    </option>
                    {categoryTypes?.map((itm, i) => {
                      return (
                        <option
                          className={`text-black`}
                          key={i}
                          value={itm?.id}
                        >
                          {itm?.name}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </div>
              <div className={`flex items-center lg:justify-between gap-4`}>
                <div className={`flex flex-col gap-7 w-full`}>
                  <div
                    className={`w-full flex lg:flex-col gap-7 max-lg:flex-wrap`}
                  >
                    <div
                      className={`w-full flex items-center gap-4 max-lg:flex-wrap`}
                    >
                      <div
                        className={`flex items-center gap-4 w-full lg:w-1/4`}
                      >
                        <span
                          className={`text-[#6f6f6f] text-sm whitespace-pre`}
                        >
                          شاخص
                        </span>
                        <input
                          {...register.getFieldProps("indicator")}
                          className={`input w-full bg-white rounded-lg border text-black ${
                            register.touched.indicator &&
                            register.errors.indicator
                              ? "border-red-500"
                              : "border-[#D6D6D6]"
                          }`}
                          placeholder="شاخص"
                          disabled={isDisabled}
                        />
                      </div>
                      <div
                        className={`flex items-center gap-4 w-full lg:w-1/4`}
                      >
                        <span
                          className={`text-[#6f6f6f] text-sm whitespace-pre`}
                        >
                          واحد اندازه گیری شاخص
                        </span>
                        <input
                          {...register.getFieldProps(
                            "indicatorMeasurementUnit"
                          )}
                          className={`input w-full bg-white rounded-lg border text-black ${
                            register.touched.indicatorMeasurementUnit &&
                            register.errors.indicatorMeasurementUnit
                              ? "border-red-500"
                              : "border-[#D6D6D6]"
                          }`}
                          placeholder="واحد اندازه گیری"
                          disabled={isDisabled}
                        />
                      </div>
                    </div>
                    <div className={`flex gap-4 w-full items-center`}>
                      <div
                        className={`flex items-center gap-2 w-full lg:w-1/3`}
                      >
                        <input
                          type="checkbox"
                          {...register.getFieldProps("employeeCreateEvent")}
                          checked={register?.values?.employeeCreateEvent}
                          className={`checkbox checkbox-primary`}
                          disabled={isDisabled || !isSystemTypeSelected}
                        />
                        <span
                          className={`text-[#6f6f6f] text-sm whitespace-pre`}
                        >
                          اعضا می‌توانند چالش تعریف کنند
                        </span>
                      </div>
                      {/* {catId && (
                        <div
                          className={`flex items-center gap-4 lg:w-1/2 pointer-events-none opacity-50`}
                        >
                          <span
                            className={`text-[#6f6f6f] text-sm whitespace-pre`}
                          >
                            ثبت اطلاعات توسط
                          </span>
                          <select
                            className={`select w-full bg-white rounded-lg border text-black ${
                              register.touched.dataEntryType &&
                              register.errors.dataEntryType
                                ? "border-red-500"
                                : "border-[#D6D6D6]"
                            }`}
                            {...register.getFieldProps("dataEntryType")}
                          >
                            <option value="SYSTEM">سیستم</option>
                            <option value="ADMIN">ادمین</option>
                            <option value="PRESENCE">اسکن/ورود کد</option>
                          </select>
                        </div>
                      )} */}
                    </div>
                  </div>
                  {/* <div className={`flex gap-4 w-full items-center`}>
                    <span
                      className={`text-[#6f6f6f] text-sm whitespace-pre tracking-tight`}
                    >
                      نحوه محاسبه
                    </span>
                    <div className={`flex flex-col gap-2 w-1/3`}>
                      <select
                        name="ngo"
                        value={register?.values?.ngo}
                        onChange={register?.handleChange}
                        className={`select select-bordered bg-white text-black w-full ${
                          register.touched.ngo && register.errors.ngo
                            ? "border-red-500"
                            : "border-[#D6D6D6]"
                        }`}
                      >
                        <option value="" disabled>
                          انتخاب کنید
                        </option>
                        <option className={`text-black`} value={"TIME"}>
                          کمتر یا مساوی هدف چالش
                        </option>
                        <option
                          className={`text-black`}
                          value={"GOAL"}
                          selected
                        >
                          بیشتر یا مساوی هدف چالش
                        </option>
                      </select>
                    </div>
                  </div> */}
                  <div className={`flex gap-4 w-full items-center`}>
                    {/* <span
                      className={`text-[#6f6f6f] text-sm whitespace-pre tracking-tight`}
                    >
                      امتیاز تعریف چالش
                    </span> */}
                    {/* <div
                      className={`flex flex-col justify-center gap-2 w-full lg:w-1/4 relative`}
                    >
                      <input
                        {...register.getFieldProps(
                          "creatorGainChallengeScorePercent"
                        )}
                        onChange={handleNumericChange(
                          "creatorGainChallengeScorePercent"
                        )}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className={`input w-full bg-white rounded-lg border text-black ${
                          register.touched.creatorGainChallengeScorePercent &&
                          register.errors.creatorGainChallengeScorePercent
                            ? "border-red-500"
                            : "border-[#D6D6D6]"
                        }`}
                        placeholder="20"
                        disabled={!register?.values?.employeeCreateEvent}
                      />
                      <span
                        className={`text-sm text-[#a9a9a9] absolute left-4`}
                      >
                        درصد از امتیاز چالش
                      </span>
                    </div> */}
                    {/* <span
                      className={`text-[#6f6f6f] text-sm whitespace-pre tracking-tight`}
                    >
                      +
                    </span> */}
                    {/* <div
                      className={`flex flex-col justify-center gap-2 w-full lg:w-1/4 relative`}
                    >
                      <input
                        {...register.getFieldProps(
                          "creatorGainParticipantsScorePercent"
                        )}
                        onChange={handleNumericChange(
                          "creatorGainParticipantsScorePercent"
                        )}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className={`input w-full bg-white rounded-lg border text-black ${
                          register.touched
                            .creatorGainParticipantsScorePercent &&
                          register.errors.creatorGainParticipantsScorePercent
                            ? "border-red-500"
                            : "border-[#D6D6D6]"
                        }`}
                        placeholder="20"
                        disabled={!register?.values?.employeeCreateEvent}
                      />
                      <span
                        className={`text-sm text-[#a9a9a9] absolute left-4`}
                      >
                        درصد از امتیاز مشارکت کنندگان
                      </span>
                    </div> */}
                  </div>
                  <div className={`flex items-center gap-4 w-full`}>
                    <span
                      className={`text-[#6f6f6f] text-sm whitespace-pre tracking-tight items-center`}
                    >
                      امتیاز شرکت در چالش
                    </span>
                    <div
                      className={`flex flex-col justify-center gap-2 w-full lg:w-1/4 relative`}
                    >
                      <input
                        {...register.getFieldProps(
                          "challengeParticipationPercent"
                        )}
                        onChange={handleNumericChange(
                          "challengeParticipationPercent"
                        )}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className={`input w-full bg-white rounded-lg border text-black ${
                          register.touched.challengeParticipationPercent &&
                          register.errors.challengeParticipationPercent
                            ? "border-red-500"
                            : "border-[#D6D6D6]"
                        }`}
                        placeholder="10"
                        disabled={isDisabled || isPresenceTypeSelected}
                      />
                      <span
                        className={`text-sm text-[#a9a9a9] absolute left-4`}
                      >
                        درصد از امتیاز چالش
                      </span>
                    </div>
                  </div>
                  <div
                    className={`flex max-lg:flex-wrap gap-4 w-full items-center`}
                  >
                    <div className={`flex items-center gap-4 max-lg:w-full`}>
                      <span
                        className={`text-[#6f6f6f] text-sm whitespace-pre tracking-tight`}
                      >
                        امتیاز نفر اول
                      </span>
                      <div
                        className={`flex flex-col justify-center gap-2 w-full relative`}
                      >
                        <input
                          {...register.getFieldProps("firstPlacePercent")}
                          onChange={handleNumericChange("firstPlacePercent")}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className={`input w-full bg-white rounded-lg border text-black ${
                            register.touched.firstPlacePercent &&
                            register.errors.firstPlacePercent
                              ? "border-red-500"
                              : "border-[#D6D6D6]"
                          }`}
                          placeholder={isPresenceTypeSelected ? 0 : 50}
                          disabled={isDisabled || isPresenceTypeSelected}
                        />
                        <span
                          className={`text-sm text-[#a9a9a9] absolute left-4`}
                        >
                          درصد از امتیاز چالش
                        </span>
                      </div>
                    </div>
                    <div className={`flex items-center gap-4 max-lg:w-full`}>
                      <span
                        className={`text-[#6f6f6f] text-sm whitespace-pre tracking-tight`}
                      >
                        امتیاز نفر دوم
                      </span>
                      <div
                        className={`flex flex-col justify-center gap-2 w-full relative`}
                      >
                        <input
                          {...register.getFieldProps("secondPlacePercent")}
                          onChange={handleNumericChange("secondPlacePercent")}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className={`input w-full bg-white rounded-lg border text-black ${
                            register.touched.secondPlacePercent &&
                            register.errors.secondPlacePercent
                              ? "border-red-500"
                              : "border-[#D6D6D6]"
                          }`}
                          placeholder={isPresenceTypeSelected ? 0 : 40}
                          disabled={isDisabled || isPresenceTypeSelected}
                        />
                        <span
                          className={`text-sm text-[#a9a9a9] absolute left-4`}
                        >
                          درصد از امتیاز چالش
                        </span>
                      </div>
                    </div>
                    <div className={`flex items-center gap-4 max-lg:w-full`}>
                      <span
                        className={`text-[#6f6f6f] text-sm whitespace-pre tracking-tight`}
                      >
                        امتیاز نفر سوم
                      </span>
                      <div
                        className={`flex flex-col justify-center gap-2 w-full relative`}
                      >
                        <input
                          {...register.getFieldProps("thirdPlacePercent")}
                          onChange={handleNumericChange("thirdPlacePercent")}
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className={`input w-full bg-white rounded-lg border text-black ${
                            register.touched.thirdPlacePercent &&
                            register.errors.thirdPlacePercent
                              ? "border-red-500"
                              : "border-[#D6D6D6]"
                          }`}
                          placeholder={isPresenceTypeSelected ? 0 : 30}
                          disabled={isDisabled || isPresenceTypeSelected}
                        />
                        <span
                          className={`text-sm text-[#a9a9a9] absolute left-4`}
                        >
                          درصد از امتیاز چالش
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div
            className={`w-full flex justify-end items-center max-lg:my-5 lg:mt-11 lg:px-10`}
          >
            <button
              type="submit"
              onClick={register.submitForm}
              disabled={isLoading}
              className={`btn bg-myBlue257 hover:bg-myBlue257 hover:scale-105 text-myNeutralBackground max-lg:w-full`}
            >
              {catId ? "ویرایش دسته‌بندی" : "ثبت دسته‌بندی چالش"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
